import { useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const SS_KEY = "gca_ai_open";

const AITransparency = () => {
  const { lang } = useLang();
  const t = (de, en) => (lang === "de" ? de : en);
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(SS_KEY) === "1"; } catch { return false; }
  });

  const setOpenPersist = (v) => {
    setOpen(v);
    try { sessionStorage.setItem(SS_KEY, v ? "1" : "0"); } catch { /* noop */ }
  };

  const points = [
    t("Formulierungen und Texte", "Wording and copy"),
    t("Kursbeschreibungen", "Course descriptions"),
    t("Bildentwürfe und Grafiken", "Image drafts and graphics"),
    t("Such- und Filterfunktionen", "Search and filter functions"),
    t("Chat- und Empfehlungssysteme", "Chat and recommendation systems"),
    t("Automatisierungen im Buchungsprozess", "Automations in the booking process"),
  ];

  return (
    <>
      <button
        onClick={() => setOpenPersist(true)}
        aria-label={t("KI-Transparenz", "AI transparency")}
        title={t("KI-Transparenz", "AI transparency")}
        data-testid="ai-transparency-btn"
        className="fixed bottom-20 md:bottom-24 right-4 md:right-6 z-[90] w-9 h-9 flex items-center justify-center bg-[#111111] border border-[#ff0033] text-[#ff0033] hover:scale-110 hover:shadow-[0_0_18px_rgba(255,0,51,0.5)] transition-transform duration-200 rounded-full"
      >
        <Sparkles size={17} />
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[210] flex items-center justify-center p-4" data-testid="ai-transparency-modal">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setOpenPersist(false)} />
          <div className="relative w-full max-w-md max-h-[85vh] overflow-y-auto bg-[#0A0A0A] border border-[#ff0033]/30 rounded-lg shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-white/10 bg-gradient-to-r from-[#ff0033]/15 to-transparent">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-[#ff0033]" />
                <h3 className="font-heading text-lg uppercase tracking-tight text-white">{t("KI-Transparenz", "AI Transparency")}</h3>
              </div>
              <button onClick={() => setOpenPersist(false)} data-testid="ai-transparency-close" aria-label={t("Schließen", "Close")} className="text-neutral-400 hover:text-white transition-colors"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-4 text-sm leading-relaxed">
              <p className="text-neutral-300">
                {t(
                  "Bei der Erstellung und Weiterentwicklung dieser Website kommen moderne Technologien der künstlichen Intelligenz (KI) unterstützend zum Einsatz.",
                  "Modern artificial intelligence (AI) technologies are used to support the creation and ongoing development of this website."
                )}
              </p>
              <div>
                <p className="text-neutral-400 mb-2">{t("Dies betrifft unter anderem:", "This includes, among others:")}</p>
                <ul className="space-y-1.5">
                  {points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-neutral-300">
                      <span className="mt-1.5 w-1.5 h-1.5 bg-[#ff0033] shrink-0 rounded-full" />{p}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-neutral-300">
                {t(
                  "Alle veröffentlichten Inhalte werden vor der Veröffentlichung von der Golfcoaching Academy Wendlingen geprüft und freigegeben.",
                  "All published content is reviewed and approved by Golfcoaching Academy Wendlingen prior to publication."
                )}
              </p>
              <p className="text-neutral-400">
                {t(
                  "Unser Ziel ist es, unseren Besuchern möglichst verständliche, aktuelle und hilfreiche Informationen bereitzustellen.",
                  "Our goal is to provide our visitors with the clearest, most up-to-date and helpful information possible."
                )}
              </p>
            </div>

            <div className="p-5 pt-0">
              <button onClick={() => setOpenPersist(false)} data-testid="ai-transparency-ok" className="w-full bg-[#ff0033] text-white font-heading uppercase tracking-wider py-3 hover:bg-[#cc0029] transition-colors">
                {t("Verstanden", "Got it")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AITransparency;
